import { type ReactElement } from 'react';
import { type VehicleCycledEnergyRecord } from '../../services/battery-monitoring-api/types/battery-monitoring-api';
import Card from '../utils/Card';
import Odometer from './Odometer';
import styles from './style.module.scss';
import MetricProgressCard from './MetricProgressCard';
import { isNumber, formatInteger, formatMaxOneDecimal } from '@/utils/number-formatters';

interface VehicleDetailProperties {
  readonly selectedVehicle: VehicleCycledEnergyRecord;
}

const MetricsGrid = ({ selectedVehicle }: VehicleDetailProperties): ReactElement => {
  const isPhev = selectedVehicle.propulsion === 'PHEV';
  const {
    cycledEnergyRatio,
    cycledEnergyKwh,
    cycledEnergyPerKmRatio,
    cycledEnergyPerKm,
    batteryLifeRatio,
    estimatedTotalYears,
    estimatedRemainingYears,
    dailyDistanceQ3Km,
    odometerTotal,
    maxCycledEnergyMwh,
  } = selectedVehicle;

  return (
    <Card className={styles.container}>
      <MetricProgressCard
        iconName="flash"
        title="Cycled Energy total"
        progressPercent={!isPhev && isNumber(cycledEnergyRatio) ? cycledEnergyRatio * 100 : undefined}
        primaryValue={isNumber(cycledEnergyKwh) ? `${formatMaxOneDecimal(cycledEnergyKwh / 1000)} MWh` : undefined}
        maxLabel={!isPhev && isNumber(maxCycledEnergyMwh) ? `${formatInteger(maxCycledEnergyMwh)} MWh` : undefined}
        minLabel="0 MWh"
        hideIncompleteFooter
      />
      <MetricProgressCard
        iconName="flash"
        title="Cycled Energy / km"
        unavailable={isPhev}
        progressPercent={!isPhev && isNumber(cycledEnergyPerKmRatio) ? cycledEnergyPerKmRatio * 100 : undefined}
        primaryValue={
          !isPhev && isNumber(cycledEnergyPerKm) ? `${formatMaxOneDecimal(cycledEnergyPerKm)} kWh/km` : undefined
        }
        maxLabel="6 kWh/km"
        minLabel="0 kWh/km"
        hideIncompleteFooter
      />
      <MetricProgressCard
        iconName="calendar"
        title="Battery Age (calendar)"
        progressPercent={isNumber(batteryLifeRatio) ? batteryLifeRatio * 100 : undefined}
        primaryValue={
          isNumber(estimatedRemainingYears) ? `${formatMaxOneDecimal(estimatedRemainingYears)} years` : undefined
        }
        maxLabel={
          isNumber(estimatedTotalYears) && estimatedTotalYears > 0
            ? `${formatMaxOneDecimal(estimatedTotalYears)} years`
            : undefined
        }
        minLabel="0 years"
      />
      <Odometer
        odometerDaily={isNumber(dailyDistanceQ3Km) ? dailyDistanceQ3Km : undefined}
        operationalFromDate={selectedVehicle.operationalFromDate}
        operationalToDate={selectedVehicle.operationalToDate}
        odometerTotal={isNumber(odometerTotal) ? odometerTotal : undefined}
      />
    </Card>
  );
};

export default MetricsGrid;
